/**
 * CR097: Hook for site state and publishing management
 * CR: Two-Step Promotion - Extended with Admin tier support
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SiteState {
  key: string;
  // Live (Public) tier - existing
  active_snapshot_id: string | null;
  active_batch_run_id: string | null;
  published_at: string | null;
  published_by: string | null;
  published_deal_count: number | null;
  // Admin tier - NEW
  admin_snapshot_id: string | null;
  admin_batch_run_id: string | null;
  admin_promoted_at: string | null;
  admin_deal_count: number | null;
  // CR210: Public deals retention policy
  public_deals_policy_mode: "normal" | "keep_expired";
  public_deals_policy_grace_days: number;
  updated_at: string;
}

export interface PublishAuditEntry {
  id: string;
  action: "PUBLISH" | "ROLLBACK" | "PROMOTE_TO_ADMIN" | "PUBLISH_TO_LIVE";
  snapshot_id: string;
  batch_run_id: string | null;
  deal_count: number | null;
  gates_passed: boolean;
  warnings_json: unknown;
  performed_by: string | null;
  performed_at: string;
  previous_snapshot_id: string | null;
  notes: string | null;
}

export interface PublishResult {
  success: boolean;
  snapshot_id?: string;
  deal_count?: number;
  gates_passed?: boolean;
  gates_failed?: Array<{ rule_id: string; rule_name: string; message: string }>;
  warnings?: Array<{ rule_id: string; rule_name: string; message: string }>;
  error?: string;
}

export interface PromoteResult {
  success: boolean;
  admin_snapshot_id?: string;
  deal_count?: number;
  upserted_count?: number;
  inserted_count?: number;
  archived_count?: number;
  error?: string;
  message?: string;
  violations?: unknown[];
}

export interface PublishLiveResult {
  success: boolean;
  public_snapshot_id?: string;
  previous_snapshot_id?: string;
  deal_count?: number;
  deduped_updated_count?: number;
  error?: string;
  message?: string;
}

export function useSiteState() {
  const [siteState, setSiteState] = useState<SiteState | null>(null);
  const [publishHistory, setPublishHistory] = useState<PublishAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const { toast } = useToast();

  const fetchSiteState = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke<SiteState>("site-state", {
        method: "GET",
      });

      if (error) throw error;
      setSiteState(data);
    } catch (err) {
      console.error("[useSiteState] Error fetching site state:", err);
    }
  }, []);

  const fetchPublishHistory = useCallback(async (limit = 10) => {
    try {
      const { data, error } = await supabase
        .from("publish_audit_log")
        .select("*")
        .order("performed_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      setPublishHistory(data as PublishAuditEntry[]);
    } catch (err) {
      console.error("[useSiteState] Error fetching publish history:", err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSiteState(), fetchPublishHistory()]);
      setLoading(false);
    };
    load();
  }, [fetchSiteState, fetchPublishHistory]);

  // Legacy publish method - still works for backward compatibility
  const publishSnapshot = useCallback(async (
    batchRunId: string,
    options?: { force?: boolean; notes?: string }
  ): Promise<PublishResult> => {
    setPublishing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke<PublishResult>("admin-publish-snapshot", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          batch_run_id: batchRunId,
          force: options?.force,
          notes: options?.notes,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Published Successfully",
          description: `Snapshot ${data.snapshot_id} is now live with ${data.deal_count} deals`,
        });
        await Promise.all([fetchSiteState(), fetchPublishHistory()]);
      } else if (data?.gates_failed && data.gates_failed.length > 0) {
        toast({
          title: "Publish Blocked",
          description: `${data.gates_failed.length} gate(s) failed. Review and fix before publishing.`,
          variant: "destructive",
        });
      }

      return data || { success: false, error: "No response" };
    } catch (err) {
      console.error("[useSiteState] Publish error:", err);
      toast({
        title: "Publish Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return { success: false, error: String(err) };
    } finally {
      setPublishing(false);
    }
  }, [toast, fetchSiteState, fetchPublishHistory]);

  // NEW: Promote staging batch → Admin snapshot
  const promoteToAdmin = useCallback(async (
    batchRunId: string,
    options?: { notes?: string }
  ): Promise<PromoteResult> => {
    setPromoting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke<PromoteResult>("batch-run-promote-admin", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          batch_run_id: batchRunId,
          notes: options?.notes,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Promoted to Admin",
          description: `${data.deal_count} deals promoted to Admin snapshot ${data.admin_snapshot_id}`,
        });
        await Promise.all([fetchSiteState(), fetchPublishHistory()]);
      } else if (data?.error) {
        toast({
          title: "Promotion Failed",
          description: data.message || data.error,
          variant: "destructive",
        });
      }

      return data || { success: false, error: "No response" };
    } catch (err) {
      console.error("[useSiteState] Promote error:", err);
      toast({
        title: "Promotion Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return { success: false, error: String(err) };
    } finally {
      setPromoting(false);
    }
  }, [toast, fetchSiteState, fetchPublishHistory]);

  // NEW: Publish Admin snapshot → Live (Public)
  const publishToLive = useCallback(async (
    options?: { notes?: string }
  ): Promise<PublishLiveResult> => {
    setPublishing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke<PublishLiveResult>("admin-publish-live", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          notes: options?.notes,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Published to Live",
          description: `Admin snapshot ${data.public_snapshot_id} is now visible to the public`,
        });
        await Promise.all([fetchSiteState(), fetchPublishHistory()]);
      } else if (data?.error) {
        toast({
          title: "Publish Failed",
          description: data.message || data.error,
          variant: "destructive",
        });
      }

      return data || { success: false, error: "No response" };
    } catch (err) {
      console.error("[useSiteState] Publish to Live error:", err);
      toast({
        title: "Publish Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return { success: false, error: String(err) };
    } finally {
      setPublishing(false);
    }
  }, [toast, fetchSiteState, fetchPublishHistory]);

  const rollbackSnapshot = useCallback(async (
    snapshotId: string,
    notes?: string
  ): Promise<boolean> => {
    setPublishing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke("admin-rollback-snapshot", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { snapshot_id: snapshotId, notes },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Rollback Successful",
          description: `Rolled back to snapshot ${snapshotId}`,
        });
        await Promise.all([fetchSiteState(), fetchPublishHistory()]);
        return true;
      }

      return false;
    } catch (err) {
      console.error("[useSiteState] Rollback error:", err);
      toast({
        title: "Rollback Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return false;
    } finally {
      setPublishing(false);
    }
  }, [toast, fetchSiteState, fetchPublishHistory]);

  return {
    siteState,
    publishHistory,
    loading,
    publishing,
    promoting,
    refetch: fetchSiteState,
    publishSnapshot,
    promoteToAdmin,
    publishToLive,
    rollbackSnapshot,
  };
}
